import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { getDateLocale } from '../../utils/dateLocale';
import {
  Search, Plus, Download, Upload, Globe, MapPin, Phone, Mail,
  Edit, Trash2, ChevronLeft, ChevronRight, Loader2, RefreshCw,
  Users, Target, CheckCircle, XCircle, Clock, Eye, Filter, X, Zap, ExternalLink
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Label } from '../ui/label';
import api from '../../services/api';
import { toast } from 'sonner';

const REGIONS = {
  ara: "Auvergne-Rhone-Alpes",
  bfc: "Bourgogne-Franche-Comte",
  bre: "Bretagne",
  cvl: "Centre-Val de Loire",
  cor: "Corse",
  ges: "Grand Est",
  hdf: "Hauts-de-France",
  idf: "Ile-de-France",
  nor: "Normandie",
  naq: "Nouvelle-Aquitaine",
  occ: "Occitanie",
  pdl: "Pays de la Loire",
  pac: "Provence-Alpes-Cote d'Azur",
};

const STATUS_COLORS = {
  nouveau: "bg-blue-100 text-blue-800",
  contacte: "bg-amber-100 text-amber-800",
  interesse: "bg-purple-100 text-purple-800",
  demo_planifiee: "bg-indigo-100 text-indigo-800",
  inscrit: "bg-green-100 text-green-800",
  refuse: "bg-red-100 text-red-800",
  inactif: "bg-slate-100 text-slate-600",
};

const STATUS_LABELS = {
  nouveau: "Nouveau",
  contacte: "Contacte",
  interesse: "Interesse",
  demo_planifiee: "Demo planifiee",
  inscrit: "Inscrit",
  refuse: "Refuse",
  inactif: "Inactif",
};

const SPORT_TYPES = [
  "Running", "Trail", "Triathlon", "Cyclisme", "Natation",
  "Marche", "Obstacle Race", "Ultra-Trail", "Cross", "Duathlon",
  "Aquathlon", "VTT", "Marathon", "Semi-Marathon", "10km",
  "Swimrun", "Canicross", "Autre"
];

const emptyProspect = {
  name: "", organization: "", email: "", phone: "", website: "",
  address: "", city: "", postal_code: "", region: "", sport_types: [],
  status: "nouveau", notes: "", source: "manuel",
};

const AdminProspectionSection = () => {
  const [prospects, setProspects] = useState([]);
  const [stats, setStats] = useState(null);
  const [existingOrgas, setExistingOrgas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Filters
  const [regionFilter, setRegionFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sportFilter, setSportFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Dialogs
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showScrapeDialog, setShowScrapeDialog] = useState(false);
  const [editingProspect, setEditingProspect] = useState(null);
  const [formData, setFormData] = useState({ ...emptyProspect });
  const [csvText, setCsvText] = useState('');
  const [scraping, setScraping] = useState(false);
  const [scrapeResults, setScrapeResults] = useState(null);
  const [enriching, setEnriching] = useState(false);
  const [enrichResults, setEnrichResults] = useState(null);

  // View mode
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'map' | 'existing'

  const fetchProspects = useCallback(async (p = 1) => {
    try {
      const params = new URLSearchParams({ page: p.toString(), limit: '30' });
      if (regionFilter && regionFilter !== 'all') params.append('region', regionFilter);
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (sportFilter && sportFilter !== 'all') params.append('sport', sportFilter);
      if (searchQuery) params.append('search', searchQuery);
      const res = await api.get(`/admin/prospects?${params}`);
      setProspects(res.data.prospects || []);
      setTotalPages(res.data.pages || 1);
      setTotal(res.data.total || 0);
      setPage(p);
    } catch (err) {
      setProspects([]);
      setTotal(0);
      setTotalPages(1);
    }
  }, [regionFilter, statusFilter, sportFilter, searchQuery]);

  const fetchStats = async () => {
    try {
      const res = await api.get('/admin/prospects/stats');
      setStats(res.data);
    } catch {
      setStats({ total: 0, by_region: {}, by_status: {}, by_source: {}, existing_organizers: 0, regions: {}, sport_types: [], status_list: [] });
    }
  };

  const fetchExistingOrgas = async (region = '') => {
    try {
      const params = (region && region !== 'all') ? `?region=${region}` : '';
      const res = await api.get(`/admin/prospects/existing-organizers${params}`);
      setExistingOrgas(res.data.organizers || []);
    } catch {
      setExistingOrgas([]);
    }
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchProspects(1), fetchStats(), fetchExistingOrgas()]);
      setLoading(false);
    };
    init();
  }, []);

  useEffect(() => {
    fetchProspects(1);
  }, [regionFilter, statusFilter, sportFilter, fetchProspects]);

  const handleSearch = () => fetchProspects(1);

  const handleSave = async () => {
    try {
      if (editingProspect) {
        await api.put(`/admin/prospects/${editingProspect.prospect_id}`, formData);
        toast.success('Prospect mis a jour');
      } else {
        await api.post('/admin/prospects', formData);
        toast.success('Prospect ajoute');
      }
      setShowAddDialog(false);
      setEditingProspect(null);
      setFormData({ ...emptyProspect });
      fetchProspects(page);
      fetchStats();
    } catch (err) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (prospectId) => {
    if (!window.confirm('Supprimer ce prospect ?')) return;
    try {
      await api.delete(`/admin/prospects/${prospectId}`);
      toast.success('Prospect supprime');
      fetchProspects(page);
      fetchStats();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleStatusChange = async (prospectId, newStatus) => {
    try {
      await api.put(`/admin/prospects/${prospectId}`, { status: newStatus });
      setProspects(prev => prev.map(p => p.prospect_id === prospectId ? { ...p, status: newStatus } : p));
      fetchStats();
    } catch {
      toast.error('Erreur');
    }
  };

  const handleImportCsv = async () => {
    try {
      const res = await api.post('/admin/prospects/import-csv', { csv_data: csvText });
      toast.success(`${res.data.imported} prospects importes, ${res.data.duplicates} doublons ignores`);
      setShowImportDialog(false);
      setCsvText('');
      fetchProspects(1);
      fetchStats();
    } catch {
      toast.error('Erreur lors de l\'import');
    }
  };

  const handleScrape = async (sourceId = 'all') => {
    setScraping(true);
    setScrapeResults(null);
    try {
      const res = await api.post('/admin/prospects/scrape', { source_id: sourceId });
      toast.success(res.data.message || 'Scraping lance en arriere-plan...');
      // Poll for scrape completion
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await api.get('/admin/prospects/scrape-status');
          const job = statusRes.data?.job;
          if (job && job.status === 'done') {
            clearInterval(pollInterval);
            setScraping(false);
            setScrapeResults({
              total_found: job.total_found,
              total_imported: job.total_imported,
              total_duplicates: job.total_duplicates,
              sources: job.source_results,
            });
            toast.success(`${job.total_imported} nouveaux prospects importes !`);
            fetchProspects(1);
            fetchStats();
          } else if (job && job.status === 'error') {
            clearInterval(pollInterval);
            setScraping(false);
            toast.error('Le scraping a rencontre une erreur');
          }
        } catch { /* ignore poll errors */ }
      }, 4000);
      // Safety timeout 5min
      setTimeout(() => { clearInterval(pollInterval); setScraping(false); fetchProspects(1); fetchStats(); }, 300000);
    } catch {
      toast.error('Erreur lors du lancement du scraping');
      setScraping(false);
    }
  };

  const handleEnrichBatch = async (region = '') => {
    setEnriching(true);
    setEnrichResults(null);
    try {
      const res = await api.post('/admin/prospects/enrich-batch', { region: (region && region !== 'all') ? region : '', limit: 20 });
      toast.success(res.data.message || 'Enrichissement lance en arriere-plan...');
      // Poll for results
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await api.get('/admin/prospects/enrich-status');
          const s = statusRes.data;
          setEnrichResults({
            emails_found: s.with_email,
            phones_found: s.with_phone,
            remaining: s.remaining_to_enrich,
            enriched_total: s.enriched_total,
          });
          if (s.latest_job) {
            const jobTime = new Date(s.latest_job.created_at).getTime();
            if (jobTime > Date.now() - 120000) {
              clearInterval(pollInterval);
              setEnriching(false);
              fetchProspects(page);
              fetchStats();
              toast.success(`Enrichissement termine — ${s.with_email} emails, ${s.with_phone} telephones au total`);
            }
          }
        } catch { /* ignore */ }
      }, 5000);
      setTimeout(() => { clearInterval(pollInterval); setEnriching(false); fetchProspects(page); }, 180000);
    } catch {
      toast.error("Erreur lors du lancement de l'enrichissement");
      setEnriching(false);
    }
  };

  const handleEnrichSingle = async (prospectId) => {
    try {
      const res = await api.post(`/admin/prospects/enrich/${prospectId}`);
      if (res.data.enriched) {
        toast.success(`Organisateur: ${res.data.organizer?.organizer_name || '?'} — Email: ${res.data.contact?.email || 'non trouve'}`);
        fetchProspects(page);
      } else {
        toast.info(res.data.message || 'Pas enrichi');
      }
    } catch {
      toast.error("Erreur d'enrichissement");
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (regionFilter && regionFilter !== 'all') params.append('region', regionFilter);
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      const res = await api.get(`/admin/prospects/export?${params}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'prospects_ibooksport.csv';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export CSV telecharge');
    } catch {
      toast.error('Erreur lors de l\'export');
    }
  };

  const openEdit = (prospect) => {
    setEditingProspect(prospect);
    setFormData({
      name: prospect.name || '',
      organization: prospect.organization || '',
      email: prospect.email || '',
      phone: prospect.phone || '',
      website: prospect.website || '',
      address: prospect.address || '',
      city: prospect.city || '',
      postal_code: prospect.postal_code || '',
      region: prospect.region || '',
      sport_types: prospect.sport_types || [],
      status: prospect.status || 'nouveau',
      notes: prospect.notes || '',
      source: prospect.source || '',
    });
    setShowAddDialog(true);
  };

  const openAdd = () => {
    setEditingProspect(null);
    setFormData({ ...emptyProspect });
    setShowAddDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-brand" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="prospection-section">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Total Prospects', value: stats?.total || 0, icon: Target, color: 'text-brand' },
          { label: 'Nouveaux', value: stats?.by_status?.nouveau || 0, icon: Clock, color: 'text-blue-600' },
          { label: 'Contactes', value: stats?.by_status?.contacte || 0, icon: Phone, color: 'text-amber-600' },
          { label: 'Inscrits', value: stats?.by_status?.inscrit || 0, icon: CheckCircle, color: 'text-green-600' },
          { label: 'Orgas iBookSport', value: stats?.existing_organizers || 0, icon: Users, color: 'text-purple-600' },
        ].map((s, i) => (
          <div key={i} className="bg-white border border-slate-200 p-3" data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="flex items-center gap-2">
              <s.icon className={`w-4 h-4 ${s.color}`} />
              <span className="text-xs text-slate-500 uppercase font-heading">{s.label}</span>
            </div>
            <p className="text-2xl font-heading font-bold mt-1">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="bg-white border border-slate-200 p-4">
        <div className="flex flex-wrap items-center gap-3">
          {/* View Mode Tabs */}
          <div className="flex border border-slate-200 rounded overflow-hidden">
            {[
              { id: 'list', label: 'Prospects' },
              { id: 'map', label: 'Par Region' },
              { id: 'existing', label: 'Orgas Existants' },
            ].map(v => (
              <button key={v.id} data-testid={`view-${v.id}`}
                className={`px-3 py-1.5 text-xs font-heading uppercase ${viewMode === v.id ? 'bg-brand text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                onClick={() => setViewMode(v.id)}
              >{v.label}</button>
            ))}
          </div>

          <div className="flex-1" />

          {/* Actions */}
          <Button size="sm" className="bg-brand text-white gap-1 text-xs" onClick={openAdd} data-testid="add-prospect-btn">
            <Plus className="w-3.5 h-3.5" /> Ajouter
          </Button>
          <Button size="sm" variant="outline" className="gap-1 text-xs" onClick={() => setShowImportDialog(true)} data-testid="import-csv-btn">
            <Upload className="w-3.5 h-3.5" /> Import CSV
          </Button>
          <Button size="sm" variant="outline" className="gap-1 text-xs" onClick={handleExport} data-testid="export-csv-btn">
            <Download className="w-3.5 h-3.5" /> Export CSV
          </Button>
          <Button size="sm" variant="outline" className="gap-1 text-xs text-purple-600 border-purple-200" onClick={() => setShowScrapeDialog(true)} data-testid="scrape-btn">
            <Globe className="w-3.5 h-3.5" /> Scraper Web
          </Button>
          <Button size="sm" variant="outline" className="gap-1 text-xs text-green-600 border-green-200" onClick={() => handleEnrichBatch(regionFilter)} disabled={enriching} data-testid="enrich-batch-btn">
            {enriching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />} Enrichir (50)
          </Button>
          {enrichResults && (
            <span className="text-[11px] text-green-600 bg-green-50 px-2 py-1 rounded" data-testid="enrich-results">
              {enrichResults.emails_found} emails + {enrichResults.phones_found} tel | {enrichResults.enriched_total || 0} enrichis | {enrichResults.remaining} restants
            </span>
          )}
        </div>

        {/* Filters */}
        {viewMode === 'list' && (
          <div className="flex flex-wrap items-center gap-3 mt-3 pt-3 border-t border-slate-100">
            <div className="relative flex-1 min-w-[200px] max-w-[300px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input placeholder="Rechercher nom, email, ville..." value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-9 h-8 text-sm" data-testid="prospect-search" />
            </div>
            <Select value={regionFilter} onValueChange={setRegionFilter}>
              <SelectTrigger className="w-[200px] h-8 text-xs" data-testid="filter-region">
                <Filter className="w-3 h-3 mr-1" />
                <SelectValue placeholder="Toutes regions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes regions</SelectItem>
                {Object.entries(REGIONS).map(([id, name]) => (
                  <SelectItem key={id} value={id}>{name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="filter-status">
                <SelectValue placeholder="Tous statuts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous statuts</SelectItem>
                {Object.entries(STATUS_LABELS).map(([id, label]) => (
                  <SelectItem key={id} value={id}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sportFilter} onValueChange={setSportFilter}>
              <SelectTrigger className="w-[150px] h-8 text-xs" data-testid="filter-sport">
                <SelectValue placeholder="Tous sports" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous sports</SelectItem>
                {SPORT_TYPES.map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(regionFilter && regionFilter !== 'all' || statusFilter && statusFilter !== 'all' || sportFilter && sportFilter !== 'all' || searchQuery) && (
              <Button variant="ghost" size="sm" className="h-8 text-xs text-slate-500" onClick={() => {
                setRegionFilter(''); setStatusFilter(''); setSportFilter(''); setSearchQuery('');
              }} data-testid="clear-filters">
                <X className="w-3 h-3 mr-1" /> Effacer filtres
              </Button>
            )}
          </div>
        )}
      </div>

      {/* LIST VIEW */}
      {viewMode === 'list' && (
        <div className="bg-white border border-slate-200">
          <div className="p-3 border-b flex items-center justify-between">
            <span className="text-sm text-slate-500">{total} prospect{total > 1 ? 's' : ''}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-3 text-xs font-heading font-bold uppercase">Organisation</th>
                  <th className="text-left p-3 text-xs font-heading font-bold uppercase">Contact</th>
                  <th className="text-left p-3 text-xs font-heading font-bold uppercase">Localisation</th>
                  <th className="text-left p-3 text-xs font-heading font-bold uppercase">Sports</th>
                  <th className="text-left p-3 text-xs font-heading font-bold uppercase">Source</th>
                  <th className="text-left p-3 text-xs font-heading font-bold uppercase">Statut</th>
                  <th className="text-left p-3 text-xs font-heading font-bold uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {prospects.length === 0 && (
                  <tr><td colSpan={7} className="p-8 text-center text-slate-400 text-sm">Aucun prospect trouve</td></tr>
                )}
                {prospects.map(p => (
                  <tr key={p.prospect_id} className="border-b hover:bg-slate-50" data-testid={`prospect-row-${p.prospect_id}`}>
                    <td className="p-3">
                      <p className="font-medium text-sm">{p.organization || p.name}</p>
                      {p.organization && p.name !== p.organization && (
                        <p className="text-xs text-slate-400">{p.name}</p>
                      )}
                      {p.organizer_website && (
                        <a href={p.organizer_website} target="_blank" rel="noopener noreferrer" className="text-[10px] flex items-center gap-0.5 text-brand mt-0.5"><ExternalLink className="w-2.5 h-2.5" />Site organisateur</a>
                      )}
                    </td>
                    <td className="p-3">
                      {p.email && <p className="text-xs flex items-center gap-1"><Mail className="w-3 h-3 text-slate-400" />{p.email}</p>}
                      {p.phone && <p className="text-xs flex items-center gap-1"><Phone className="w-3 h-3 text-slate-400" />{p.phone}</p>}
                      {p.website && <a href={p.website.startsWith('http') ? p.website : `https://${p.website}`} target="_blank" rel="noopener noreferrer" className="text-xs flex items-center gap-1 text-brand"><Globe className="w-3 h-3" />Site web</a>}
                    </td>
                    <td className="p-3">
                      <p className="text-xs flex items-center gap-1"><MapPin className="w-3 h-3 text-slate-400" />{[p.city, p.postal_code].filter(Boolean).join(' ') || '-'}</p>
                      {p.region && <p className="text-xs text-slate-400">{REGIONS[p.region] || p.region}</p>}
                    </td>
                    <td className="p-3">
                      <div className="flex flex-wrap gap-1">
                        {(p.sport_types || []).slice(0, 3).map((s, i) => (
                          <span key={i} className="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded">{s}</span>
                        ))}
                        {(p.sport_types || []).length > 3 && <span className="text-[10px] text-slate-400">+{p.sport_types.length - 3}</span>}
                      </div>
                    </td>
                    <td className="p-3">
                      <span className="text-xs text-slate-500">{p.source || '-'}</span>
                    </td>
                    <td className="p-3">
                      <Select value={p.status} onValueChange={(v) => handleStatusChange(p.prospect_id, v)}>
                        <SelectTrigger className={`h-7 text-[11px] w-[130px] border-0 ${STATUS_COLORS[p.status] || ''}`} data-testid={`status-${p.prospect_id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(STATUS_LABELS).map(([id, label]) => (
                            <SelectItem key={id} value={id}>{label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-3">
                      <div className="flex gap-1">
                        {!p.enriched && p.website?.includes('milesrepublic') && (
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-green-600" onClick={() => handleEnrichSingle(p.prospect_id)} data-testid={`enrich-${p.prospect_id}`} title="Enrichir (trouver coordonnees)">
                            <Zap className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(p)} data-testid={`edit-${p.prospect_id}`}>
                          <Edit className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDelete(p.prospect_id)} data-testid={`delete-${p.prospect_id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="p-3 border-t flex items-center justify-between">
              <Button variant="outline" size="sm" disabled={page === 1} onClick={() => fetchProspects(page - 1)}>
                <ChevronLeft className="w-4 h-4" /> Prec.
              </Button>
              <span className="text-sm text-slate-500">Page {page} / {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => fetchProspects(page + 1)}>
                Suiv. <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      )}

      {/* MAP VIEW */}
      {viewMode === 'map' && (
        <div className="bg-white border border-slate-200 p-4">
          <h3 className="font-heading font-bold uppercase text-sm mb-4">Prospects par Region</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {Object.entries(REGIONS).map(([id, name]) => {
              const count = stats?.by_region?.[id]?.count || 0;
              return (
                <div key={id}
                  className={`border p-3 cursor-pointer transition-all hover:border-brand hover:shadow-sm ${regionFilter === id ? 'border-brand bg-brand/5' : 'border-slate-200'}`}
                  onClick={() => { setRegionFilter(id); setViewMode('list'); }}
                  data-testid={`region-card-${id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-heading font-bold text-sm">{name}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{count} prospect{count > 1 ? 's' : ''}</p>
                    </div>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-heading font-bold text-sm ${count > 0 ? 'bg-brand/10 text-brand' : 'bg-slate-100 text-slate-400'}`}>
                      {count}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* EXISTING ORGANIZERS VIEW */}
      {viewMode === 'existing' && (
        <div className="bg-white border border-slate-200">
          <div className="p-3 border-b flex items-center justify-between">
            <h3 className="font-heading font-bold uppercase text-sm">Organisateurs iBookSport ({existingOrgas.length})</h3>
            <Select value={regionFilter} onValueChange={(v) => { setRegionFilter(v); fetchExistingOrgas(v === 'all' ? '' : v); }}>
              <SelectTrigger className="w-[200px] h-8 text-xs">
                <SelectValue placeholder="Toutes regions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes regions</SelectItem>
                {Object.entries(REGIONS).map(([id, name]) => (
                  <SelectItem key={id} value={id}>{name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-green-50">
                <tr>
                  <th className="text-left p-3 text-xs font-heading font-bold uppercase">Organisateur</th>
                  <th className="text-left p-3 text-xs font-heading font-bold uppercase">Email</th>
                  <th className="text-left p-3 text-xs font-heading font-bold uppercase">Telephone</th>
                  <th className="text-left p-3 text-xs font-heading font-bold uppercase">Ville</th>
                  <th className="text-left p-3 text-xs font-heading font-bold uppercase">Evenements</th>
                  <th className="text-left p-3 text-xs font-heading font-bold uppercase">Statut</th>
                </tr>
              </thead>
              <tbody>
                {existingOrgas.length === 0 && (
                  <tr><td colSpan={6} className="p-8 text-center text-slate-400 text-sm">Aucun organisateur trouve</td></tr>
                )}
                {existingOrgas.map(o => (
                  <tr key={o.user_id} className="border-b hover:bg-slate-50" data-testid={`orga-row-${o.user_id}`}>
                    <td className="p-3">
                      <p className="font-medium text-sm">{o.company_name || o.name}</p>
                    </td>
                    <td className="p-3 text-xs">{o.email}</td>
                    <td className="p-3 text-xs">{o.phone || '-'}</td>
                    <td className="p-3 text-xs">{o.city || '-'}</td>
                    <td className="p-3">
                      <span className="text-xs font-medium">{o.event_count}</span>
                    </td>
                    <td className="p-3">
                      <span className={`text-[11px] px-2 py-0.5 rounded ${o.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-600'}`}>
                        {o.status === 'active' ? 'Actif' : o.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ADD/EDIT DIALOG */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase">
              {editingProspect ? 'Modifier le prospect' : 'Ajouter un prospect'}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div>
              <Label className="text-xs">Nom du contact</Label>
              <Input value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                placeholder="Jean Dupont" className="h-8 text-sm" data-testid="form-name" />
            </div>
            <div>
              <Label className="text-xs">Organisation</Label>
              <Input value={formData.organization} onChange={e => setFormData(p => ({ ...p, organization: e.target.value }))}
                placeholder="Association Running Lyon" className="h-8 text-sm" data-testid="form-organization" />
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input type="email" value={formData.email} onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                placeholder="contact@example.com" className="h-8 text-sm" data-testid="form-email" />
            </div>
            <div>
              <Label className="text-xs">Telephone</Label>
              <Input value={formData.phone} onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))}
                placeholder="06 12 34 56 78" className="h-8 text-sm" data-testid="form-phone" />
            </div>
            <div>
              <Label className="text-xs">Site web</Label>
              <Input value={formData.website} onChange={e => setFormData(p => ({ ...p, website: e.target.value }))}
                placeholder="https://..." className="h-8 text-sm" data-testid="form-website" />
            </div>
            <div>
              <Label className="text-xs">Adresse</Label>
              <Input value={formData.address} onChange={e => setFormData(p => ({ ...p, address: e.target.value }))}
                placeholder="12 rue du Sport" className="h-8 text-sm" data-testid="form-address" />
            </div>
            <div>
              <Label className="text-xs">Ville</Label>
              <Input value={formData.city} onChange={e => setFormData(p => ({ ...p, city: e.target.value }))}
                placeholder="Lyon" className="h-8 text-sm" data-testid="form-city" />
            </div>
            <div>
              <Label className="text-xs">Code postal</Label>
              <Input value={formData.postal_code} onChange={e => setFormData(p => ({ ...p, postal_code: e.target.value }))}
                placeholder="69000" className="h-8 text-sm" data-testid="form-postal" />
            </div>
            <div>
              <Label className="text-xs">Region</Label>
              <Select value={formData.region} onValueChange={v => setFormData(p => ({ ...p, region: v }))}>
                <SelectTrigger className="h-8 text-sm" data-testid="form-region">
                  <SelectValue placeholder="Selectionner..." />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(REGIONS).map(([id, name]) => (
                    <SelectItem key={id} value={id}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Statut</Label>
              <Select value={formData.status} onValueChange={v => setFormData(p => ({ ...p, status: v }))}>
                <SelectTrigger className="h-8 text-sm" data-testid="form-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_LABELS).map(([id, label]) => (
                    <SelectItem key={id} value={id}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Sports (cliquez pour selectionner)</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {SPORT_TYPES.map(s => (
                  <button key={s} type="button"
                    className={`text-[11px] px-2 py-1 rounded border transition-colors ${formData.sport_types.includes(s) ? 'bg-brand text-white border-brand' : 'bg-white text-slate-600 border-slate-200 hover:border-brand'}`}
                    onClick={() => setFormData(p => ({
                      ...p,
                      sport_types: p.sport_types.includes(s) ? p.sport_types.filter(x => x !== s) : [...p.sport_types, s]
                    }))}
                    data-testid={`sport-tag-${s}`}
                  >{s}</button>
                ))}
              </div>
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Notes</Label>
              <textarea value={formData.notes} onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))}
                className="w-full border border-slate-200 rounded p-2 text-sm h-20 resize-none"
                placeholder="Notes internes..." data-testid="form-notes" />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Annuler</Button>
            <Button className="bg-brand text-white" onClick={handleSave} data-testid="save-prospect-btn">
              {editingProspect ? 'Mettre a jour' : 'Ajouter'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* IMPORT CSV DIALOG */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase">Import CSV</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">
              Format attendu : colonnes <code>name, organization, email, phone, website, address, city, postal_code, region, sports, notes</code>
            </p>
            <textarea
              value={csvText}
              onChange={e => setCsvText(e.target.value)}
              className="w-full border border-slate-200 rounded p-2 text-xs font-mono h-40 resize-none"
              placeholder="name,organization,email,phone,city,postal_code,sports&#10;Jean Dupont,Running Lyon,contact@rl.fr,0612345678,Lyon,69000,Running"
              data-testid="csv-input"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowImportDialog(false)}>Annuler</Button>
              <Button className="bg-brand text-white" onClick={handleImportCsv} disabled={!csvText.trim()} data-testid="import-confirm-btn">
                <Upload className="w-3.5 h-3.5 mr-1" /> Importer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* SCRAPE DIALOG */}
      <Dialog open={showScrapeDialog} onOpenChange={setShowScrapeDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase">Scraper des sources web</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">
              Recherche automatique d'organisateurs depuis des calendriers sportifs publics (FFA, FFTri, Jogging-Plus...).
            </p>
            <div className="space-y-2">
              {[
                { id: 'protiming', name: 'ProTiming.fr - Calendrier Courses', sport: 'Running / Toutes courses' },
                { id: 'protiming_trail', name: 'ProTiming.fr - Trail', sport: 'Trail' },
                { id: 'milesrepublic_trail', name: 'MilesRepublic - Trail par Region', sport: 'Trail (13 regions)' },
                { id: 'milesrepublic_running', name: 'MilesRepublic - Running par Region', sport: 'Running (13 regions)' },
              ].map(src => (
                <div key={src.id} className="flex items-center justify-between border border-slate-200 p-2 rounded">
                  <div>
                    <p className="text-sm font-medium">{src.name}</p>
                    <p className="text-xs text-slate-400">{src.sport}</p>
                  </div>
                  <Button size="sm" variant="outline" className="text-xs" disabled={scraping}
                    onClick={() => handleScrape(src.id)} data-testid={`scrape-${src.id}`}>
                    {scraping ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                    Scraper
                  </Button>
                </div>
              ))}
            </div>
            <Button className="w-full bg-brand text-white gap-1" disabled={scraping} onClick={() => handleScrape('all')} data-testid="scrape-all-btn">
              {scraping ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />}
              {scraping ? 'Scraping en cours...' : 'Scraper toutes les sources'}
            </Button>
            {scrapeResults && (
              <div className="bg-slate-50 border border-slate-200 rounded p-3 mt-2">
                <p className="text-sm font-medium">Resultats du scraping :</p>
                <p className="text-xs text-slate-600 mt-1">
                  {scrapeResults.total_found} trouves | {scrapeResults.total_imported} importes | {scrapeResults.total_duplicates} doublons
                </p>
                {scrapeResults.sources?.map((s, i) => (
                  <p key={i} className="text-[11px] text-slate-500 mt-0.5">
                    {s.source}: {s.found} trouves, {s.imported} importes
                  </p>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminProspectionSection;
